<template>
  <ul class="car-numericals grid grid-cols-2 gap-4" v-if="carDetail">
    <li class="car-numerical flex flex-col items-center gap-4 p-4 border border-accent rounded-sm" v-for="(carNumerical, i) in carNumericals" :key="i">
      <img :src="carNumerical.iconSrc" :alt="carNumerical.label" loading="lazy" width="40" height="40" class="car-numerical__icon size-10 object-contain object-center" />
      <span class="car-numerical__label sr-only">{{ carNumerical.label }}</span>
      <span class="car-numerical__value text-medium-normal text-center" v-html="carNumerical.value"></span>
    </li>
  </ul>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';

  const { t, locale, locales } = useI18n();
  const carDetail = inject<GraphQLCarDetail>('carDetail');

  const currentLanguage = computed(() => locales.value.find(l => l.code === locale.value)?.language || 'de-DE');

  const carNumericals = computed(() => {
    const carNumericalsData: { label: string; value: string; iconSrc: string }[] = [];
    if (!carDetail) return carNumericalsData;

    if (carDetail.first_registration) {
      const formattedFirstRegistration = new Date(carDetail.first_registration).toLocaleDateString(currentLanguage.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.firstRegistration'),
        value: formattedFirstRegistration,
        iconSrc: '/imgs/svgs/car-details/first-registration.svg',
      });
    }

    if (carDetail.mileage) {
      const formattedMileage = carDetail.mileage.toLocaleString(currentLanguage.value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.mileage'),
        value: formattedMileage + ' km',
        iconSrc: '/imgs/svgs/car-details/mileage.svg',
      });
    }

    if (carDetail.power_kw || carDetail.power_hp) {
      let formattedPower = '';
      if (carDetail.power_hp) {
        formattedPower =
          carDetail.power_hp.toLocaleString(currentLanguage.value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' PS';
      }

      if (carDetail.power_hp && carDetail.power_kw) {
        formattedPower += ' / ';
      }

      if (carDetail.power_kw) {
        formattedPower +=
          carDetail.power_kw.toLocaleString(currentLanguage.value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kW';
      }
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.power'),
        value: formattedPower,
        iconSrc: '/imgs/svgs/car-details/power.svg',
      });
    }

    if (carDetail.fuel_type) {
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.fuelType'),
        value: carDetail.fuel_type,
        iconSrc: '/imgs/svgs/car-details/fuel-type.svg',
      });
    }

    if (carDetail.drive_type) {
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.driveType'),
        value: carDetail.drive_type,
        iconSrc: '/imgs/svgs/car-details/drive-type.svg',
      });
    }
    if (carDetail.transmission_type) {
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.transmissionType'),
        value: carDetail.transmission_type,
        iconSrc: '/imgs/svgs/car-details/transmission-type.svg',
      });
    }

    if (carDetail.keyNumber || carDetail.location) {
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.location'),
        value: getKeyNumber(carDetail.keyNumber || ''),
        iconSrc: '/imgs/svgs/car-details/location.svg',
      });
    }

    if (carDetail.vat_deductible !== null) {
      carNumericalsData.push({
        label: t('components.carDetail.carNumericals.label.vatDeductible'),
        value: carDetail.vat_deductible ? t('components.carDetail.carNumericals.label.yes') : t('components.carDetail.carNumericals.label.no'),
        iconSrc: '/imgs/svgs/car-details/vat-deductible.svg',
      });
    }

    return carNumericalsData;
  });

  const getKeyNumber = (string: string) => {
    if (string.startsWith('OW')) {
      return string.replace('OW', 'Oberwart ');
    }

    if (string.startsWith('HO')) {
      return string.replace('HO', 'Hofstätten ');
    }

    if (string.startsWith('GL')) {
      return string.replace('GL', 'Gleisdorf ');
    }

    if (carDetail?.location) {
      let city = carDetail.location.split(' ').filter(Boolean)[1]?.trim();
      city = city ? city?.charAt(0).toUpperCase() + city?.slice(1) : '';

      if (!string && city) {
        return city;
      }

      if (city && string) {
        return `<span class="block">${city}</span><span class="block">${string}</span>`;
      }
    }
    return string;
  };
</script>

<style lang="scss" scoped></style>
